// Voice actions
import axios from 'axios';

// Action creators using the existing voice reducer actions
import { setCallInfo, setCustomer, setOngoingCalls } from '../reducers/voiceReducer';
import { findActiveCustomerCall } from '../utils/activeCustomerCall';

// A call this tab is running through the Twilio SDK. `status` belongs to the
// SDK event listeners in FloatingStatusBar for the whole life of the call.
const LOCAL_CALL_STATUSES = new Set(['connecting', 'ringing', 'answered']);

const getCallUserId = (call) => {
	if (!call?._user) return null;
	return typeof call._user === 'object' ? call._user?._id : call._user;
};

// Fetch current voice status from the server.
//
// This refresh only ever supplies server-side detail (the ongoing-calls list,
// and the Call record behind the call this tab is on). It never writes
// `status`: the Call record lags and leads the browser's own leg, so copying
// it across used to show "Connected" while the agent still heard ringing, drop
// a ringing call back to 'connecting' (re-arming the 30s setup watchdog, which
// then hung up a live call), and leave tabs that never held the call stuck on
// a phantom 'answered' that blocked their next call. A failed refresh is not a
// failed call either, so it must not flip the bar to 'error'.
export const fetchCurrentVoiceStatus = () => async (dispatch, getState) => {
	try {
		const res = await axios.get('/api/voice/status');
		const { ongoingCalls } = res.data;

		// Always update the ongoing calls array
		dispatch(setOngoingCalls(ongoingCalls || []));

		const state = getState();
		if (!LOCAL_CALL_STATUSES.has(state.voice.status)) return res.data;
		if (!ongoingCalls?.length) return res.data;

		const currentCallSid = state.voice.callInfo?.callSid;
		const currentUserId = state.auth?.user?._id;
		const localCustomer = state.voice.customer;
		const isOwnCall = (call) =>
			!!currentUserId && getCallUserId(call)?.toString() === currentUserId.toString();
		const ownCalls = ongoingCalls.filter(isOwnCall);
		const hasLocalCustomer = Boolean(localCustomer?._id || localCustomer?.phone);

		// The record for this tab's call is recognised by its customer first:
		// the SDK only learns an outbound CallSid once the call is ringing, and an
		// accepted inbound call reports its own leg's sid, not the record's. A
		// sid match comes second so a record adopted too early (a call this user
		// holds on another device) is dropped as soon as the right one appears.
		const customerCall = hasLocalCustomer
			? findActiveCustomerCall(ownCalls, localCustomer)
			: undefined;
		const sidCall = currentCallSid
			? ownCalls.find((call) => call.callSid === currentCallSid)
			: undefined;
		const currentCall =
			customerCall ||
			sidCall ||
			(!hasLocalCustomer && ownCalls.length === 1 ? ownCalls[0] : null);

		if (currentCall) {
			dispatch(setCallInfo(currentCall));
			// Fill in the server's customer record, but never swap the bar to a
			// different customer (e.g. a call this user holds on another device).
			const isSameCustomer = !hasLocalCustomer || currentCall === customerCall;
			if (currentCall._customer && isSameCustomer) {
				dispatch(setCustomer(currentCall._customer));
			}
		}

		return res.data;
	} catch (error) {
		console.error('Error fetching voice status:', error);
		throw error;
	}
};
